import * as yup from "yup";
import type { FieldSchema, ValidationRule } from "../../types/workflow-schema";
import { getAddressSubFields } from "../../types/workflow-schema";

/** US 5-digit zip or Canadian A1A 1A1 (space required) */
const POSTAL_REGEX = /^(\d{5}|[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d)$/;
const POSTAL_MESSAGE = "Must be a valid US zip or Canadian postal code";

/**
 * Converts field schemas from Sanity into a Yup validation schema.
 * Used by the SchemaFormRenderer to generate runtime validation.
 *
 * Composite field types (address) expand into multiple sub-field schemas.
 */
export const buildYupSchema = (
  fields: FieldSchema[],
): yup.ObjectSchema<Record<string, unknown>> => {
  const shape: Record<string, yup.Schema> = {};

  for (const field of fields) {
    // Address is a composite field — expand into sub-field validations
    if (field.type === "address") {
      for (const sub of getAddressSubFields(field.name)) {
        // .trim() so whitespace-only values fail required()
        let subSchema = yup.string().trim();
        if (field.required && sub.required) {
          subSchema = subSchema.required(`${sub.label} is required`);
        }
        // Postal sub-fields: US 5-digit zip or Canadian A1A 1A1 format
        if (sub.name === "postal" || sub.name.endsWith("Postal")) {
          subSchema = subSchema.matches(POSTAL_REGEX, POSTAL_MESSAGE);
        }
        shape[sub.name] = subSchema;
      }
      continue;
    }

    // postal-input — always gates on servicePostal (confirmed by API), never field.name
    if (field.type === "postal-input") {
      shape["servicePostal"] = yup
        .string()
        .required(`${field.label || "Postal / Zip Code"} is required`);
      continue;
    }

    let schema = getBaseSchema(field.type);

    if (field.required) {
      schema = (schema as yup.StringSchema).required(
        `${field.label} is required`,
      );
    }

    if (field.validation) {
      schema = applyValidationRule(schema, field.validation, field.label);
    }

    shape[field.name] = schema;
  }

  return yup.object(shape);
};

const getBaseSchema = (type: string): yup.Schema => {
  switch (type) {
    case "email":
      return yup
        .string()
        .email("Must be a valid email address")
        .test(
          "no-spaces",
          "Email cannot contain spaces",
          (val) => !val || !val.includes(" "),
        );
    case "number":
    case "increment":
      return yup.number();
    case "checkbox":
      return yup.boolean();
    case "date":
    case "time":
    case "calendar":
      return yup.string();
    case "hidden":
      // Hidden fields carry a fixed value — no validation needed
      return yup.mixed();
    case "select":
      // FormNewSelect stores the full option object { value, label }.
      // Transform it down to just the value string before Yup validates.
      return yup
        .string()
        .transform((val: unknown) =>
          typeof val === "object" && val !== null && "value" in val
            ? (val as any).value
            : val,
        );
    case "phone":
      // Phone mask may store formatted value like "(123) 456-7890".
      // Strip non-digits and require exactly 10.
      return yup
        .string()
        .test(
          "phone-digits",
          "Must be a valid 10-digit phone number",
          (val) => {
            if (!val) return true; // let required() handle empty
            const digits = val.replace(/\D/g, "");
            return digits.length === 10;
          },
        );
    case "text":
    case "textarea":
      // .trim() so whitespace-only values fail required()
      return yup.string().trim();
    case "radio":
    case "tile-select":
    default:
      return yup.string();
  }
};

const applyValidationRule = (
  schema: yup.Schema,
  rule: ValidationRule,
  label: string,
): yup.Schema => {
  const message = rule.message || `${label} is invalid`;

  switch (rule.type) {
    case "minLength":
      return (schema as yup.StringSchema).min(Number(rule.value), message);
    case "maxLength":
      return (schema as yup.StringSchema).max(Number(rule.value), message);
    case "pattern":
      return (schema as yup.StringSchema).matches(
        new RegExp(String(rule.value)),
        message,
      );
    case "phone":
      return (schema as yup.StringSchema).test(
        "phone-digits",
        message || "Must be a valid 10-digit phone number",
        (val) => {
          if (!val) return true;
          const digits = val.replace(/\D/g, "");
          return digits.length === 10;
        },
      );
    case "postal":
      return (schema as yup.StringSchema).matches(
        POSTAL_REGEX,
        message || POSTAL_MESSAGE,
      );
    default:
      return schema;
  }
};
